import { object, string } from 'yup';
import { PaymentCheckout } from '../../pages';
import { catalogStore } from '../../stores';
import React, { useEffect, useMemo, useState } from 'react';
import { MimaToastUtil } from '../../components';
import constant from '../../utils/constant';
import * as yup from 'yup';
import { phoneRegExp, removeEmptyKeys } from '../../utils/utils';
import { StyleCatalogController } from '../../controllers';
import { useNavigate } from 'react-router-dom';
import { usePaystackPayment } from 'react-paystack';
import { loadStripe } from '@stripe/stripe-js';
import useGetSubdomain from '../../hooks/useGetSubdomain';

const PaymentCheckoutContainer = () => {
	const [loading, setLoading] = useState(false);
	const [invoice, setInvoice] = useState({});
	const [clientSessionId, setClientSessionId] = useState('');
	const [foreignAccount, setForeignAccount] = useState({});

	const { catalogOrderInfo, primaryLocation, setCatalogOrderInfo } =
		catalogStore || {};

	const { subdomain } = useGetSubdomain();

	const validationSchema = () => {
		return object({
			firstname: yup
				.string()
				.min(2, 'First name is required')
				.required('First name is required'),
			lastname: yup
				.string()
				.min(2, 'Last name is required')
				.required('Last name is required'),
			email: string()
				.trim()
				.email('Enter a valid Email')
				.required('Email Address is required'),
			mobile: yup
				.string()
				.matches(phoneRegExp, 'Phone number is not valid')
				.required('phone number is required'),
			street: yup.string().required('Address is required'),
			country: yup.string().required('Country is required'),
			postalCode: yup.string().optional(),
			company: yup.string().optional(),
			apartment: yup.string().optional(),
			city: yup.string().required('City is required'),
		});
	};

	const paystackConfig = useMemo(() => {
		const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

		const metadata = {
			business: invoice?.business?._id,
			invoiceNumber: `INV-${invoice?.number}`,
			customer: { fullname: invoice?.customer?.fullname },
			invoice: invoice?._id,
			type: 'INVOICE',
		};

		const config = {
			email: invoice?.customer?.email,
			amount: invoice?.transactionAmount * 100,
			metadata,
			publicKey,
		};
		return config;
	}, [invoice]);

	const onSuccess = () => {
		if (foreignAccount?.code) {
			MimaToastUtil.success({
				message:
					'Payment is in verification, Your order will be processed once confirmed, Check your email for confirmation',
			});
		} else {
			MimaToastUtil.success({
				message:
					'Payment made Successfully, Your order is being processed, Check your email for confirmation',
			});
		}

		localStorage.removeItem('fashCart');
		setCatalogOrderInfo({});
		return navigate(constant.Routes.Home);
	};

	const onClose = () => {
		return MimaToastUtil.error({ message: 'Payment Failed, Try again' });
	};

	const initializePaystackPayment = usePaystackPayment(paystackConfig);

	const sPublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
	const stripePromise = loadStripe(sPublicKey);

	useEffect(() => {
		if (invoice?.currencyCode === 'NGN') {
			initializePaystackPayment(onSuccess, onClose);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [invoice]);

	const uniteDateAndTime = (dateString, timeString) => {
		// Parse the date
		const date = new Date(dateString);

		// Extract hours and minutes from the time string
		const timeParts = timeString.match(/(\d+):(\d+)(am|pm)?/i);

		if (!timeParts) {
			throw new Error('Invalid time format');
		}

		let hours = parseInt(timeParts[1], 10);
		const minutes = parseInt(timeParts[2], 10);
		const period = timeParts[3]?.toLowerCase();

		// Adjust for AM/PM if present
		if (period === 'pm' && hours !== 12) {
			hours += 12;
		} else if (period === 'am' && hours === 12) {
			hours = 0;
		}

		// Set the hours and minutes on the date
		date.setHours(hours, minutes, 0, 0);

		return date;
	};

	const onSubmit = async values => {
		setLoading(true);

		// Never charge a stale price from the stored cart
		const {
			cart: freshOrderInfo,
			changed,
			failed,
		} = await StyleCatalogController.refreshCartPrices(
			catalogOrderInfo,
			subdomain
		);

		if (failed) {
			setLoading(false);
			return MimaToastUtil.error({
				message: 'Unable to confirm current prices, please try again',
			});
		}

		if (changed) {
			setCatalogOrderInfo(freshOrderInfo);
			localStorage.setItem('fashCart', JSON.stringify(freshOrderInfo));
			setLoading(false);
			if (freshOrderInfo.length === 0) {
				MimaToastUtil.error({
					message: 'The items in your cart are no longer available',
				});
				return navigate(constant.Routes.WebsiteCart);
			}
			return MimaToastUtil.error({
				message:
					'Some prices in your order have changed, please review your order and try again',
			});
		}

		const orders = freshOrderInfo.map(item => {
			return {
				item: item?.name,
				style: item?.styleId,
				standardSize: item?.standardSize,
				quantity: item?.quantity,
				unitPrice: item?.amountPerItem,
				color: item?.color,
				accessories: item?.accessories,
				variant: item?.selectedVariant?._id,
			};
		});

		removeEmptyKeys(orders);

		let bookingPayload;

		const payload = {
			customer: {
				fullname: `${values.firstname} ${values.lastname}`,
				email: values.email,
				mobile: values.mobile,
				street: `${values.street} ${values.apartment}`,
				country: values.country,
				companyName: values.company,
				postCode: values.postalCode,
				state: values.city,
			},
			isFash: true,
			publicKey: catalogOrderInfo[0]?.business?.publicKey,
			invoice: {
				orders: [...orders],
				orderId: catalogOrderInfo[0]?.styleId,
				currencyCode: catalogOrderInfo[0]?.currencyCode,
			},
		};

		if (catalogOrderInfo[0]?.type !== 'PRODUCT') {
			bookingPayload = {
				fullname: `${values.firstname} ${values.lastname}`,
				email: values.email,
				mobile: values.mobile,
				street: `${values.street} ${values.apartment}`,
				country: values.country,
				// companyName: values.company,
				postCode: values.postalCode,
				state: values.city,
				business: catalogOrderInfo[0]?.business?._id,
				currencyCode: catalogOrderInfo[0]?.currencyCode,
				bookings: catalogOrderInfo?.map(item => {
					return {
						// item: item?.name,
						style: item?.styleId,
						// standardSize: item?.standardSize,
						// quantity: item?.quantity,
						// unitPrice: item?.amountPerItem,
						// color: item?.color,
						// accessories: item?.accessories,
						variant: item?.selectedVariant?._id,
						sessionDateTime: uniteDateAndTime(
							item?.selectedDate,
							item?.selectedTime
						),
						// note: ''
					};
				}),
			};
		}

		removeEmptyKeys(payload);
		// removeEmptyKeys(bookingPayload);

		const { status, errorMessage, data } =
			catalogOrderInfo[0]?.type === 'PRODUCT'
				? await StyleCatalogController.createCataloguePaymentCheckout(payload)
				: await StyleCatalogController.bookingCheckout(bookingPayload);

		if (status === constant.Success) {
			// MimaToastUtil.success({
			// 	message: 'Navigating to Payment Page',
			// });
			setInvoice(data);
			window.sendTrackingEvent(constant.events.checkout, {
				content_ids: data?.orders?.map(item => item._id),
				content_type: 'product',
				value: data?.deposit,
				currency: data?.currencyCode,
				num_items: data?.orders?.length,
				transaction_id: data?._id,
				order_id: data?.number,
			});
			if (data?.stripeSessionId) {
				setClientSessionId(data?.stripeSessionId);
			}
			if (data?.foreignAccount?.id?.metadata?.data?.length > 0) {
				setForeignAccount(data?.foreignAccount);
			}

			setLoading(false);
		} else {
			setLoading(false);
			MimaToastUtil.error({ message: errorMessage });
		}
	};

	const navigate = useNavigate();

	const goToCart = () => {
		navigate(constant.Routes.WebsiteCart);
	};
	const goToHome = () => {
		navigate(constant.Routes.Home);
	};

	const goBack = () => {
		setClientSessionId('');
		setForeignAccount({});
	};

	return (
		<PaymentCheckout
			loading={loading}
			validationSchema={validationSchema}
			onSubmit={onSubmit}
			catalogOrderInfo={catalogOrderInfo}
			goToCart={goToCart}
			invoice={invoice}
			goToHome={goToHome}
			clientSessionId={clientSessionId}
			stripePromise={stripePromise}
			onSuccess={onSuccess}
			onClose={onClose}
			goBack={goBack}
			foreignAccount={foreignAccount}
			primaryLocation={primaryLocation}
		/>
	);
};

export default PaymentCheckoutContainer;
